import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  FileText,
  Download,
  Eye,
  Edit,
  Plus,
  Trash2,
  Clock,
  LayoutTemplate,
  AlertTriangle,
  X,
  CheckCircle,
  Star,
  Calendar,
  Sparkles,
  FileDown,
  ChevronDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { safeStorage } from '@/lib/safe-storage';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface Resume {
  id: string;
  title: string;
  template: string;
  lastModified: string;
  status: 'draft' | 'published';
}

export function ResumeSection() {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [selectedResumeData, setSelectedResumeData] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = safeStorage.getItem('megajobnepal_auth_token');
        if (!token) return;
        const data = await apiClient.request('/resumes');
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        const mapped: Resume[] = list.map((r: any) => ({
          id: r._id || r.id,
          title: r.title || 'Untitled Resume',
          template: r.template || 'modern',
          lastModified: r.updatedAt || r.updated_at || new Date().toISOString(),
          status: (r.status === 'published' ? 'published' : 'draft')
        }));
        if (!cancelled) setResumes(mapped);
      } catch (err) {
        console.warn('Failed to fetch resumes from backend:', err);
        // Fallback: load saved resumes from localStorage if backend fails
        const savedResumes = safeStorage.getItem('resumes');
        if (savedResumes) {
          try { setResumes(JSON.parse(savedResumes)); } catch {}
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCreateResume = async () => {
    try {
      if (resumes.length >= 5) {
        toast.error('You can only create up to 5 resumes per account');
        return;
      }
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      const payload = { title: 'My Resume', template: 'modern' };
      const response = await apiClient.request('/resumes', { method: 'POST', body: JSON.stringify(payload) });
      const created = (response && (response.data ?? response)) as any;
      const newId = created?._id || created?.id;
      if (!newId) throw new Error('Invalid resume created');
      navigate(`/resume-builder/${newId}`);
    } catch (err) {
      console.error('Create resume error:', err);
      const msg =
        ((err as any)?.data && ((err as any).data.errors?.[0] || (err as any).data.message)) ||
        (err as any)?.message ||
        'Failed to create resume';
      toast.error(msg);
    }
  };

  const handleEditResume = (id: string) => {
    navigate(`/resume-builder/${id}`);
  };

  const handleDeleteResume = (resume: Resume) => {
    setSelectedResume(resume);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedResume) return;
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) throw new Error('Not authenticated');
      const response = await apiClient.request(`/resumes/${selectedResume.id}`, { method: 'DELETE' });
      const updated = resumes.filter(r => r.id !== selectedResume.id);
      setResumes(updated);
      toast.success('Resume deleted successfully');
    } catch (err) {
      console.error('Delete resume error:', err);
      const msg = (err as any)?.message || 'Failed to delete resume';
      toast.error(msg);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedResume(null);
    }
  };

  const handlePreviewResume = async (resume: Resume) => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) throw new Error('Authentication required');
      const response = await apiClient.request(`/resumes/${resume.id}`);
      const data = (response as any)?.data ?? response;
      setSelectedResumeData(data);
      setSelectedResume(resume);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load resume data');
    }
  };

  const handleDownloadResume = async (resume: Resume, format: 'pdf' | 'docx' = 'pdf') => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      toast.loading(`Generating ${format.toUpperCase()}...`);
      const path = format === 'docx'
        ? `/resumes/${resume.id}/export/docx?template=${encodeURIComponent(resume.template)}`
        : `/resumes/${resume.id}/export/pdf`;
      const blob = await apiClient.requestBlob(path, { method: 'GET' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resume.title || 'resume'}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.dismiss();
      toast.success(`Resume downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.dismiss();
      console.error('Download error:', error);
      toast.error('Failed to download resume. Please try again.');
    }
  };

  const getTemplateName = (template: string) => {
    const templates: Record<string, string> = {
      modern: 'Modern',
      professional: 'Professional',
      classic: 'Classic',
      creative: 'Creative'
    };
    return templates[template] || 'Modern';
  };

  return (
    <div className="space-y-8">
      {/* Header with Gradient Background */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h2 className="text-3xl font-bold">Resume Management</h2>
              </div>
              <p className="text-blue-100 text-lg">
                Build and manage your professional resume with 4 stunning templates
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">{resumes.length} Resumes</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <LayoutTemplate className="h-4 w-4" />
                  <span className="text-sm">4 Templates</span>
                </div>
              </div>
            </div>
            <Button 
              onClick={handleCreateResume}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Resume
            </Button>
          </div>
        </div>
        {/* Decorative circles */}
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
      </div>

      {/* Available Templates Showcase */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { name: 'Modern', desc: 'Clean & Contemporary', color: 'from-blue-500 to-cyan-500', icon: '✨' },
          { name: 'Professional', desc: 'ATS-Friendly', color: 'from-purple-500 to-pink-500', icon: '💼' },
          { name: 'Classic', desc: 'Traditional', color: 'from-orange-500 to-red-500', icon: '📝' },
          { name: 'Creative', desc: 'Bold & Unique', color: 'from-green-500 to-teal-500', icon: '🎨' }
        ].map((template) => (
          <Card key={template.name} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-blue-200">
            <CardContent className="p-5">
              <div className={`w-12 h-12 bg-gradient-to-br ${template.color} rounded-xl flex items-center justify-center mb-3 text-2xl shadow-lg`}>
                {template.icon}
              </div>
              <p className="font-semibold text-gray-900 mb-1">{template.name}</p>
              <p className="text-xs text-gray-500">{template.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* My Resumes Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">My Resumes</h3>
            <p className="text-gray-500 mt-1">Manage and download your professional resumes</p>
          </div>
          {resumes.length > 0 && (
            <Badge variant="outline" className="px-4 py-2 text-lg">
              {resumes.length} {resumes.length === 1 ? 'Resume' : 'Resumes'}
            </Badge>
          )}
        </div>
        
        {resumes.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50">
            <CardContent className="p-16 text-center">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No resumes yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Create your first professional resume with our easy-to-use builder and stand out to employers
              </p>
              <Button onClick={handleCreateResume} size="lg" className="shadow-lg">
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Resume
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resumes.map((resume) => {
              const templateColors = {
                modern: 'from-blue-500 to-cyan-500',
                professional: 'from-purple-500 to-pink-500',
                classic: 'from-orange-500 to-red-500',
                creative: 'from-green-500 to-teal-500'
              };
              const color = templateColors[resume.template as keyof typeof templateColors] || 'from-blue-500 to-cyan-500';
              
              return (
                <Card key={resume.id} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-blue-300 overflow-hidden">
                  {/* Template Header */}
                  <div className={`h-24 bg-gradient-to-br ${color} relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-white text-gray-900 shadow-lg">
                        {resume.status === 'published' ? (
                          <><Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" /> Published</>
                        ) : (
                          <><Edit className="h-3 w-3 mr-1" /> Draft</>
                        )}
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 left-4 text-white">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        <span className="font-semibold">{getTemplateName(resume.template)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {resume.title || 'Untitled Resume'}
                    </h3>

                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Calendar className="h-4 w-4" />
                      <span>
                        Last modified {new Date(resume.lastModified).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        onClick={() => handleEditResume(resume.id)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handlePreviewResume(resume)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleDownloadResume(resume, 'pdf')}>
                            <FileDown className="h-4 w-4 mr-2" />
                            Download as PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadResume(resume, 'docx')}>
                            <FileDown className="h-4 w-4 mr-2" />
                            Download as DOCX
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteResume(resume)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Start Guide */}
      <Card className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-100">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-blue-600" />
            How to Create Your Perfect Resume
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Choose Template', desc: 'Pick from 4 professional designs', icon: LayoutTemplate },
              { step: 2, title: 'Fill Information', desc: 'Add your details and experience', icon: Edit },
              { step: 3, title: 'Preview & Edit', desc: 'See live preview and refine', icon: Eye },
              { step: 4, title: 'Download', desc: 'Get your PDF resume', icon: Download }
            ].map((item) => (
              <div key={item.step} className="text-center bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <div className="mb-3">
                  <item.icon className="h-6 w-6 text-blue-600 mx-auto" />
                </div>
                <p className="font-bold text-gray-900 mb-2">{item.title}</p>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <DialogTitle className="text-xl">Delete Resume?</DialogTitle>
            </div>
            <DialogDescription className="text-base">
              Are you sure you want to delete <strong>"{selectedResume?.title || 'Untitled Resume'}"</strong>?
              <br />
              <span className="text-red-600 font-medium">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Resume
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl">{selectedResume?.title || 'Resume Preview'}</DialogTitle>
                <DialogDescription>
                  Template: {selectedResume && getTemplateName(selectedResume.template)}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewDialogOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          
          {selectedResumeData && (
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-inner">
              {/* Resume Preview Content */}
              <div className="space-y-6">
                {/* Personal Info */}
                {selectedResumeData.personalInfo && (
                  <div className="text-center border-b pb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedResumeData.personalInfo.fullName || 'Your Name'}
                    </h1>
                    <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                      {selectedResumeData.personalInfo.email && (
                        <span>{selectedResumeData.personalInfo.email}</span>
                      )}
                      {selectedResumeData.personalInfo.phone && (
                        <span>{selectedResumeData.personalInfo.phone}</span>
                      )}
                      {selectedResumeData.personalInfo.city && (
                        <span>{selectedResumeData.personalInfo.city}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {selectedResumeData.summary && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Professional Summary</h2>
                    <p className="text-gray-700 leading-relaxed">{selectedResumeData.summary}</p>
                  </div>
                )}

                {/* Experience */}
                {selectedResumeData.experience && selectedResumeData.experience.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Work Experience</h2>
                    <div className="space-y-4">
                      {selectedResumeData.experience.map((exp: any, idx: number) => (
                        <div key={idx} className="border-l-4 border-blue-500 pl-4">
                          <h3 className="font-bold text-lg">{exp.jobTitle}</h3>
                          <p className="text-gray-600">{exp.company}</p>
                          <p className="text-sm text-gray-500">{exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate}</p>
                          <p className="mt-2 text-gray-700">{exp.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {selectedResumeData.education && selectedResumeData.education.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Education</h2>
                    <div className="space-y-3">
                      {selectedResumeData.education.map((edu: any, idx: number) => (
                        <div key={idx}>
                          <h3 className="font-bold">{edu.degree} - {edu.fieldOfStudy}</h3>
                          <p className="text-gray-600">{edu.institution}</p>
                          <p className="text-sm text-gray-500">{edu.startDate} - {edu.isCurrentlyStudying ? 'Present' : edu.endDate}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {selectedResumeData.skills && selectedResumeData.skills.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Skills</h2>
                    <div className="flex flex-wrap gap-2">
                      {selectedResumeData.skills.map((skill: any, idx: number) => (
                        <Badge key={idx} variant="outline" className="px-3 py-1">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => selectedResume && handleEditResume(selectedResume.id)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Resume
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                  <ChevronDown className="h-3 w-3 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => selectedResume && handleDownloadResume(selectedResume, 'pdf')}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Download as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => selectedResume && handleDownloadResume(selectedResume, 'docx')}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Download as DOCX
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
